import { z } from 'zod';
import { hexSha256Schema, isoUtcDateTimeSchema, microAmountSchema, type MicroAmount } from '../validation/primitives.js';

export const PILOT_MODES = [
  'SIMULATION',
  'CLOSED_PILOT',
  'SANDBOX',
  'RESTRICTED_COMMERCIAL',
  'REGULATED_MARKET',
] as const;
export type PilotMode = (typeof PILOT_MODES)[number];

export const CUSTODY_MODELS = ['SELF', 'QUALIFIED'] as const;
export type CustodyModel = (typeof CUSTODY_MODELS)[number];

export const SECONDARY_TRADING_MODES = ['NONE', 'CLOSED_AUCTION', 'ORDER_BOOK'] as const;
export type SecondaryTradingMode = (typeof SECONDARY_TRADING_MODES)[number];

export const pilotScopeEnvelopeSchema = z.object({
  version: z.number().int().positive(),
  mode: z.enum(PILOT_MODES),
  sponsorAuthorityRef: z.string().min(1),
  allowedJurisdictions: z.array(z.string().length(2)), // ISO-3166-1 alpha-2
  allowedAssetClasses: z.array(z.string().min(1)),
  participantAllowlistHash: hexSha256Schema,
  maxParticipantCount: z.number().int().positive(),
  maxTransactionValueMicro: microAmountSchema,
  maxOutstandingValueMicro: microAmountSchema,
  custodyModelRequired: z.enum(CUSTODY_MODELS),
  secondaryTrading: z.enum(SECONDARY_TRADING_MODES),
  effectiveFrom: isoUtcDateTimeSchema,
  effectiveTo: isoUtcDateTimeSchema,
  governanceSignature: z.string().min(64),
});

export type PilotScopeEnvelope = z.infer<typeof pilotScopeEnvelopeSchema>;

/** Raw wire format before string->bigint transformation */
export interface PilotScopeEnvelopeWire {
  version: number;
  mode: PilotMode;
  sponsorAuthorityRef: string;
  allowedJurisdictions: string[];
  allowedAssetClasses: string[];
  participantAllowlistHash: string;
  maxParticipantCount: number;
  maxTransactionValueMicro: string;
  maxOutstandingValueMicro: string;
  custodyModelRequired: CustodyModel;
  secondaryTrading: SecondaryTradingMode;
  effectiveFrom: string;
  effectiveTo: string;
  governanceSignature: string;
}
